import { useCallback, useImperativeHandle } from 'react';

import { uniqBy } from 'lodash-es';

import { ILocation } from 'hooks/use-map/types';
import { CoordinateTypes } from 'state/geolocation';

import { FocusConfig, MapViewRef, StoreLocatorAction } from '../components/types';
import { IStore } from '../ducks/stores';

import { GetCoordinatesByFocusReasonProps, MapFocusProps } from './types';

export const getValidPoint = (store: IStore | null) => {
  if (!store?.latitude || !store?.longitude) {
    return null;
  }

  const point = {
    lat: store.latitude,
    lng: store.longitude,
  };

  return point;
};

const collectValidPoints = (points: (ILocation | null)[]): ILocation[] =>
  uniqBy(
    points.filter((point): point is ILocation => Boolean(point?.lat && point?.lng)),
    'lat'
  );

const getPointsByFocusReason = (
  focusReason: StoreLocatorAction,
  {
    coordinatesFromProps,
    getCoordinatesByPriority,
    restaurantFocused,
    closestStore,
    restaurantToFocusByProps,
    activeStore,
    userCoordinates,
  }: GetCoordinatesByFocusReasonProps
): ILocation[] => {
  // Always prioritize focus on the closest store
  const storeToFocus = restaurantToFocusByProps ?? closestStore ?? activeStore ?? restaurantFocused;

  const configs = {
    [StoreLocatorAction.isInitial]: {
      // If the store locator is in its initial state, we focus on the currently active store—unlike other focus reasons,
      // where we always prioritize the nearest one.
      anchorPoint: coordinatesFromProps ?? getCoordinatesByPriority(CoordinateTypes.USER),
      storePoint: getValidPoint(activeStore ?? storeToFocus),
    },
    [StoreLocatorAction.isReset]: {
      anchorPoint: coordinatesFromProps ?? getCoordinatesByPriority(CoordinateTypes.USER),
      storePoint: getValidPoint(storeToFocus),
    },
    [StoreLocatorAction.isManualSearch]: {
      anchorPoint: coordinatesFromProps ?? getCoordinatesByPriority(CoordinateTypes.MANUAL),
      storePoint: getValidPoint(storeToFocus),
    },
    [StoreLocatorAction.isSearching]: {
      anchorPoint:
        coordinatesFromProps ?? getCoordinatesByPriority(CoordinateTypes.SEARCH_THIS_AREA),
      storePoint: getValidPoint(storeToFocus),
    },
    [StoreLocatorAction.isUserSelection]: {
      anchorPoint: coordinatesFromProps ?? getCoordinatesByPriority(CoordinateTypes.USER),
      storePoint: getValidPoint(storeToFocus ?? null),
    },
  };

  const { anchorPoint, storePoint } = configs[focusReason] ?? {
    anchorPoint: null,
    storePoint: null,
  };

  // If no user coordinates are provided, use the fallback point to get the anchor user coordinates
  // This is to ensure that the user's location is always visible on the map.
  const fallbackPoint =
    restaurantToFocusByProps ?? activeStore ?? restaurantFocused ?? closestStore;
  const anchorUserPoint = userCoordinates ?? getValidPoint(fallbackPoint);

  const points = collectValidPoints([storePoint, anchorPoint, anchorUserPoint]);

  return points;
};

/**
 * Hook to manage store highlighting on the map
 */
export const useMapFocus = (
  ref: React.ForwardedRef<MapViewRef>,
  {
    mapComponent,
    activeStore,
    restaurantFocused,
    closestStore,
    userCoordinates,
    focusToPoint,
    focusToPoints,
    getCoordinatesByPriority,
  }: MapFocusProps
) => {
  /**
   * Handles the focus logic for the map
   * @param restaurant - The restaurant to focus on
   * @param focusReason - The reason for the focus [isInitial, isReset, isManualSearch, isSearching, isUserSelection]
   */
  const focus = useCallback(
    ({ restaurant, focusReason, coordinates: coordinatesFromProps }: FocusConfig) => {
      if (!mapComponent || !focusReason) {
        return;
      }

      const points = getPointsByFocusReason(focusReason, {
        coordinatesFromProps,
        getCoordinatesByPriority,
        closestStore,
        restaurantFocused,
        restaurantToFocusByProps: restaurant,
        activeStore,
        userCoordinates,
      });

      points.length > 1 ? focusToPoints(points) : focusToPoint(points[0]);
    },
    [
      mapComponent,
      getCoordinatesByPriority,
      closestStore,
      restaurantFocused,
      activeStore,
      userCoordinates,
      focusToPoints,
      focusToPoint,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      focus: (config: FocusConfig) => focus(config),
      mapIsReady: mapComponent !== null,
    }),
    [focus, mapComponent]
  );

  return { focus };
};
